import type { ResourceModel } from '@bryntum/schedulerpro';
import type { QueryClient } from '@tanstack/react-query';
import moment, { Moment } from 'moment';
import { Dictionary } from 'ramda';
import { MutableRefObject } from 'react';
import { getInitialAllocatedMinutes } from 'api/resource-management/constants';
import {
  AllocationMedium,
  ListOfResourcesToBeFetched,
  ResourceManagementApiZoomType,
  ResourceManagementGroupBy,
  ResourcesToBeFetched,
  TeamLevelAggregation,
  UserLevelAggregation,
} from 'api/resource-management/types';
import { Minutes } from 'components/organisms/user-capacity/types';
import {
  getAllocationAggregationKey,
  getResourceSummariesKey,
} from 'queries/resource-management';
import { unreachable } from 'utils/unreachable';
import {
  BaseEvent,
  ResourceAllocationSummaryEvent,
  SchedulerEventType,
} from '../event-types';
import { AllocationEventModel } from '../scheduler-config/models';
import { ViewPresetId } from '../scheduler-config/view-presets/constants';
import {
  AggregationLookup,
  AllocatedMinutes,
  DetailedAllocationData,
  GroupBy,
  ResourceSummariesData,
  SchedulerReference,
} from '../types';

export const minimumDate = moment().subtract(10, 'year').startOf('year');
export const maximumDate = moment().add(10, 'year').endOf('year');

export const concat = <T, U>(array1: T[], array2: U[]): (T | U)[] =>
  // hack to avoid spread operator
  array1.concat(array2 as unknown as T[]);

export const INVALID_ROLE_ID = -1;

export const getInitialResourcesToBeFetched = <
  T extends ListOfResourcesToBeFetched,
>(): ResourcesToBeFetched<T> => ({
  userIdList: [] as unknown as T,
  roleIdList: [] as unknown as T,
  projectIdList: [] as unknown as T,
});

export const getInitialResourcesToBeFetchedDictionary =
  (): ResourcesToBeFetched<Dictionary<boolean>> => ({
    userIdList: {},
    roleIdList: {},
    projectIdList: {},
  });

export const getTotalAllocatedMinutes = (
  allocatedMinutes: AllocatedMinutes,
  ignoreSoft = false,
): Minutes =>
  allocatedMinutes[AllocationMedium.Hard] +
  (ignoreSoft ? 0 : allocatedMinutes[AllocationMedium.Soft]);

export const getDetailedAllocationData = (
  allocatedMinutes: AllocatedMinutes,
  capacityMinutes = 0,
): DetailedAllocationData => {
  const totalAllocatedMinutes = getTotalAllocatedMinutes(allocatedMinutes);
  const hardAllocatedMinutes = getTotalAllocatedMinutes(allocatedMinutes, true);
  const softAllocatedMinutes = totalAllocatedMinutes - hardAllocatedMinutes;
  const availableMinutes = capacityMinutes - totalAllocatedMinutes;
  const allocatedPercentage =
    capacityMinutes === 0
      ? totalAllocatedMinutes !== 0
        ? 101
        : 100
      : Math.round((totalAllocatedMinutes / capacityMinutes) * 100);

  return {
    totalAllocatedMinutes,
    hardAllocatedMinutes,
    softAllocatedMinutes,
    allocatedPercentage,
    availableMinutes,
  };
};

export const getAllocatedMinutesFromAggregations = (
  aggregations: (UserLevelAggregation | TeamLevelAggregation)[],
): AllocatedMinutes =>
  aggregations.reduce(
    (totalAllocatedMinutes, aggregation) => ({
      [AllocationMedium.Hard]:
        totalAllocatedMinutes[AllocationMedium.Hard] +
        aggregation.allocatedMintutes[AllocationMedium.Hard],
      [AllocationMedium.Soft]:
        totalAllocatedMinutes[AllocationMedium.Soft] +
        aggregation.allocatedMintutes[AllocationMedium.Soft],
    }),
    getInitialAllocatedMinutes(),
  );

export const asApiGroupBy = (groupBy: GroupBy): ResourceManagementGroupBy => {
  switch (groupBy) {
    case GroupBy.Users:
      return ResourceManagementGroupBy.Users;
    case GroupBy.Projects:
      return ResourceManagementGroupBy.Projects;
    default:
      return unreachable(groupBy);
  }
};

export const asApiZoomType = (
  viewPreset: ViewPresetId,
): ResourceManagementApiZoomType => {
  switch (viewPreset) {
    case ViewPresetId.Month:
    case ViewPresetId.MonthZoomed:
      return ResourceManagementApiZoomType.Month;
    case ViewPresetId.Week:
    case ViewPresetId.WeekZoomed:
      return ResourceManagementApiZoomType.Week;
    case ViewPresetId.Day:
    case ViewPresetId.DayZoomed:
      return ResourceManagementApiZoomType.Day;
    default:
      return unreachable(viewPreset);
  }
};

export const addEventsToScheduler = (
  events: BaseEvent[],
  scheduler: MutableRefObject<any>,
) => {
  const nonDuplicateEvents = events.filter(
    (event) => !scheduler.current?.instance.eventStore.isAvailable(event.id),
  );

  if (nonDuplicateEvents.length > 0) {
    scheduler.current?.instance.eventStore.add(nonDuplicateEvents);
  }
};
/**
 * Determines if Saturday and Sunday should be included based on date range logic:
 * - If single date: include Saturday if that date is Saturday, include Sunday if that date is Sunday
 * - If 2-day range: include both if the range contains both Saturday and Sunday
 * @param startDate - Start date of the range (Moment object)
 * @param endDate - End date of the range (Moment object)
 * @returns Object with includeSaturday and includeSunday boolean values
 */
export const getWeekendInclusionFlags = (
  startDate: Moment,
  endDate: Moment,
): { includeSaturday: boolean; includeSunday: boolean } => {
  const startMoment = startDate.clone().startOf('day');
  const endMoment = endDate.clone().startOf('day');
  const daysDiff = endMoment.diff(startMoment, 'days');

  // Single date case
  if (daysDiff === 0) {
    const dayOfWeek = startMoment.day(); // 0 = Sunday, 6 = Saturday

    return {
      includeSaturday: dayOfWeek === 6,
      includeSunday: dayOfWeek === 0,
    };
  }

  // Two-day range case
  if (daysDiff === 1) {
    const startDayOfWeek = startMoment.day();
    const endDayOfWeek = endMoment.day();
    const hasSaturday = startDayOfWeek === 6 || endDayOfWeek === 6;
    const hasSunday = startDayOfWeek === 0 || endDayOfWeek === 0;

    // Include both only if the range contains both Saturday and Sunday
    if (hasSaturday && hasSunday) {
      return {
        includeSaturday: true,
        includeSunday: true,
      };
    }
  }

  // Default: don't include either
  return {
    includeSaturday: false,
    includeSunday: false,
  };
};

/**
 * Expands a date range to include all allocation/requested-allocation events
 * on the same resource (excluding the current event). Repeatedly fetches events
 * in the current range and expands to their min start / max end until stable.
 *
 * @param schedulerRef - Scheduler ref (instance.eventStore, instance.resourceStore)
 * @param resource - Resource record from resourceStore.getById
 * @param startDate - Initial range start
 * @param endDate - Initial range end
 * @returns Expanded start and end dates as Moments
 */
export const expandAllocationDateRange = (
  schedulerRef: SchedulerReference,
  resource: { id: string | number },
  startDate: Moment,
  endDate: Moment,
): { expandedStartDate: Moment; expandedEndDate: Moment } => {
  let fromDate = startDate.clone();
  let toDate = endDate.clone();
  const instance = schedulerRef.current?.instance;

  if (instance == null) {
    return { expandedStartDate: fromDate, expandedEndDate: toDate };
  }

  let hasChanged: boolean;

  do {
    const previousFromDate = fromDate.clone();
    const previousToDate = toDate.clone();

    const events = instance.eventStore.getEvents({
      resourceRecord: resource,
      startDate: fromDate.toDate(),
      endDate: toDate.toDate(),
      filter: (event: { type: string; id?: string | number }) => {
        const isAllocation =
          event.type === SchedulerEventType.Allocation ||
          event.type === SchedulerEventType.RequestedAllocation;

        return isAllocation;
      },
    });

    if (events.length > 0) {
      const startDates = events.map((event: { startDate: Date }) =>
        moment(event.startDate).startOf('day'),
      );
      const endDates = events.map((event: { endDate: Date }) =>
        moment(event.endDate).endOf('day'),
      );

      fromDate = moment.min(startDates);
      toDate = moment.max(endDates);
    }

    hasChanged =
      !fromDate.isSame(previousFromDate, 'day') ||
      !toDate.isSame(previousToDate, 'day');
  } while (hasChanged);

  return {
    expandedStartDate: moment.min(fromDate, startDate),
    expandedEndDate: moment.max(toDate, endDate),
  };
};

const isAllocationSummaryEventType = (type: string): boolean =>
  type === SchedulerEventType.ResourceAllocationSummary;

const isBudgetOrProjectAllocationSummaryType = (type: string): boolean =>
  type === SchedulerEventType.BudgetAllocationSummary ||
  type === SchedulerEventType.ProjectAllocationSummary;

export const getAllocationSummaryEventsToDelete = (
  schedulerRef: SchedulerReference,
  resourceRecord: ResourceModel,
  expandedStartDate: Moment,
  expandedEndDate: Moment,
): AllocationEventModel[] => {
  const instance = schedulerRef.current?.instance;

  if (instance == null) {
    return [];
  }

  const startDate = expandedStartDate.toDate();
  const endDate = expandedEndDate.toDate();

  const fetchSummariesOnResource = (
    record: ResourceModel,
    filter: (event: { type: string }) => boolean,
  ): AllocationEventModel[] =>
    instance.eventStore.getEvents({
      resourceRecord: record,
      startDate,
      endDate,
      filter,
    }) as AllocationEventModel[];

  const onLeaf = fetchSummariesOnResource(resourceRecord, (event) =>
    isAllocationSummaryEventType(event.type),
  );

  const parentRecord = resourceRecord.parent as ResourceModel | null;

  if (parentRecord == null) {
    return onLeaf;
  }

  const onParent = fetchSummariesOnResource(parentRecord, (event) =>
    isBudgetOrProjectAllocationSummaryType(event.type),
  );

  const byId = new Map<string, AllocationEventModel>();

  [...onLeaf, ...onParent].forEach((event) => {
    byId.set(String(event.id), event);
  });

  return Array.from(byId.values());
};

export const clearSchedulerEventStore = (
  schedulerRef: SchedulerReference,
): void => {
  if (schedulerRef?.current?.instance?.eventStore == null) {
    return;
  }

  const { instance } = schedulerRef.current;

  instance.suspendRefresh();
  instance.eventStore.removeAll();
  instance.resumeRefresh(true);
};

const EMPTY_RESOURCE_SUMMARIES: ResourceSummariesData = {
  userSummaryMap: {},
  roleSummaryMap: {},
  companySummaryMap: {},
  childrenBreakdown: {},
  budgetUserBreakdown: {},
};

export const clearAllocationCachesAndRowTotalsForRmFilterChange = (
  queryClient: QueryClient,
  schedulerRef: SchedulerReference,
): void => {
  queryClient.setQueryData<AggregationLookup>(
    getAllocationAggregationKey(),
    {},
  );
  queryClient.setQueryData<ResourceSummariesData>(getResourceSummariesKey(), {
    ...EMPTY_RESOURCE_SUMMARIES,
  });

  const store = schedulerRef.current?.instance?.resourceStore;

  if (store == null) {
    return;
  }

  store.forEach((resource) => {
    if (resource.get('totalAllocation') !== undefined) {
      resource.set('totalAllocation', 0);
    }
  });
};

export const withSchedulerBatchUpdate = (
  schedulerRef: SchedulerReference,
  fn: () => void,
) => {
  schedulerRef.current.instance.suspendRefresh();
  schedulerRef.current.instance.resourceStore.beginBatch();
  try {
    fn();
  } finally {
    schedulerRef.current.instance.resumeRefresh(true);
    schedulerRef.current.instance.resourceStore.endBatch();
  }
};

export const upsertSchedulerEvents = (
  events: BaseEvent[],
  schedulerRef: SchedulerReference,
): void => {
  const instance = schedulerRef.current?.instance;

  if (instance == null) {
    return;
  }

  const { eventStore } = instance;
  const existingToRemove = new Set<unknown>();

  events.forEach((incoming) => {
    const existing = eventStore.getById(incoming.id);

    if (existing == null) {
      return;
    }

    // check the existing summary events and the new summary events and remove the existing summary events if the new summary events have more allocated minutes
    const incomingAllocatedMinutes = (
      incoming as ResourceAllocationSummaryEvent
    ).allocatedMinutes;
    const incomingMinutes =
      incomingAllocatedMinutes != null
        ? getTotalAllocatedMinutes(incomingAllocatedMinutes)
        : 0;

    const existingAllocatedMinutes = existing.get('allocatedMinutes');
    const existingMinutes =
      existingAllocatedMinutes != null
        ? getTotalAllocatedMinutes(existingAllocatedMinutes)
        : 0;

    if (incomingMinutes > existingMinutes) {
      existingToRemove.add(existing);
    }
  });

  if (existingToRemove.size > 0) {
    withSchedulerBatchUpdate(schedulerRef, () => {
      eventStore.remove([...existingToRemove]);
    });
  }
};

export const isResourceAvailable = (
  schedulerRef: SchedulerReference,
  resource: { id: string },
) => !!schedulerRef.current.instance.resourceStore.getById(resource.id);
